import type { UserTabValue } from '@/features/user-console/tab-registry';
import { DIRECTION_LABELS } from '@/services/api/funds-transactions';
import type { SearchMatchType, SearchUser, SearchUserMatch } from '@/services/api/users';
import { truncateZeros } from '@/utils/format';

export interface MatchSummary {
  /** What kind of record matched, e.g. "Withdrawal address" */
  kind: string;
  /** Distinguishing detail, e.g. "BTC · deactivated" or "0.5 BTC · DONE" */
  detail: string;
  /** User console tab holding the matched record */
  tab: UserTabValue;
}

const MATCH_TYPE_LABELS: Record<SearchMatchType, string> = {
  WALLET_ADDRESS: 'Wallet address',
  TRANSACTION_HASH: 'Transaction hash',
};

const joinDetail = (parts: (string | null | undefined)[]): string => parts.filter(Boolean).join(' · ');

const capitalise = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

export const summariseMatch = (match: SearchUserMatch): MatchSummary => {
  switch (match.source) {
    case 'PAYOUT_ACCOUNT':
      return { kind: 'Withdrawal address', detail: joinDetail([match.currency, match.state?.toLowerCase()]), tab: 'payoutAccounts' };
    case 'DEPOSIT_ADDRESS':
      // Deposit addresses have no tab of their own; the deposits they received live under transactions
      return { kind: 'Deposit address', detail: joinDetail([match.currency, match.isActive === false ? 'inactive' : 'active']), tab: 'transactions' };
    case 'TRANSACTION': {
      const amount = [match.amount ? truncateZeros(match.amount) : null, match.currency].filter(Boolean).join(' ');
      return {
        kind: match.direction ? capitalise(DIRECTION_LABELS[match.direction]) : 'Transaction',
        detail: joinDetail([amount, match.transferStatus]),
        tab: 'transactions',
      };
    }
  }
};

// tab-registry hides these for broker clients, and the console silently falls back to the default tab for a
// hidden one, so a deep link there would land the investigator somewhere else without saying so.
const TABS_HIDDEN_FOR_BROKER_CLIENTS: readonly UserTabValue[] = ['payoutAccounts'];

/** User page link for a match: deep-links to the tab unless that tab is hidden for this user. */
export const matchLinkFor = (user: Pick<SearchUser, 'pid' | 'isBroker'>, tab: UserTabValue): string => {
  const isHidden = user.isBroker && TABS_HIDDEN_FOR_BROKER_CLIENTS.includes(tab);
  return isHidden ? `/operations/users/${user.pid}` : `/operations/users/${user.pid}?tab=${tab}`;
};

/** Header label for a Blockchain search once results are in: what the value turned out to be. */
export const describeBlockchainMatch = (users: SearchUser[] | undefined): string | null => {
  const types = [...new Set((users ?? []).flatMap((user) => user.matches ?? []).map((match) => match.matchType))];
  if (!types.length) return null;
  return types.map((type) => MATCH_TYPE_LABELS[type]).join(' & ');
};
